import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

import { usePagination, useQueryParams, useNotification } from '@/hooks';
import { Table, NavigateLinks, BackToModuleButton } from '@/components';
import { FeeServiceName } from '@/pages/Fleet/Businesses/components';
import { ColumnConfig } from '@/components/table/Table.tsx';
import { FeeService } from '@/pages/Fleet/Businesses/models';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { extractTime } from '@/utils';

import { SearchBar } from '../../components';
import { childrenAssignmentTemplatesPageBreadcrumbs } from '../../config';
import { useAssignments } from '../../hooks';
import { AssignmentsFilter } from '../../hooks/useAssignments';
import { AssignmentType } from '../../models/Assignment';
import { useDeleteAssignmentMutation } from '../../services/assignments.service';

import styles from '../../../FleetPages.module.css';

const CalendarAssignmentsPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const breadcrumbs = useMemo(
    () => childrenAssignmentTemplatesPageBreadcrumbs(navigate),
    []
  );

  const { getQueryParam, setQueryParams } = useQueryParams();
  const [filter, setFilter] = useState<AssignmentsFilter>({});
  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    usePagination({
      initialPage: parseInt(getQueryParam('page') || '1'),
      initialPageSize: parseInt(getQueryParam('pageSize') || '10'),
    });

  useEffect(() => {
    setQueryParams({
      page: currentPage.toString(),
      pageSize: pageSize.toString(),
    });
  }, [currentPage, pageSize]);

  const {
    isLoading,
    assignments: rawAssignments,
    totalItems,
    totalPages,
  } = useAssignments({
    type: AssignmentType.CALENDAR,
    filters: filter,
    page: currentPage,
    pageSize,
  });

  const assignments = rawAssignments.map((assignment) => ({
    ...assignment,
    monday: assignment.monday ?? false,
    tuesday: assignment.tuesday ?? false,
    wednesday: assignment.wednesday ?? false,
    thursday: assignment.thursday ?? false,
    friday: assignment.friday ?? false,
    saturday: assignment.saturday ?? false,
    sunday: assignment.sunday ?? false,
    repeat_until: assignment.repeat_until ?? 'No definido',
  }));

  const [deleteAssignment] = useDeleteAssignmentMutation();

  const onDeleteAssignment = (id: string) => {
    if (confirm('¿Estás seguro de eliminar esta asignación recurrente?')) {
      deleteAssignment({ assignmentId: id })
        .unwrap()
        .then(() =>
          showNotification(
            'Asignación eliminada correctamente',
            NotificationSeverity.success
          )
        )
        .catch(() =>
          showNotification(
            'Error al eliminar la asignación',
            NotificationSeverity.error
          )
        );
    }
  };

  const columns: ColumnConfig[] = [
    {
      label: 'Servicio',
      field: 'feeService',
      render: (feeService: FeeService) => (
        <FeeServiceName name={feeService.name} />
      ),
    },
    {
      label: 'Inicio',
      field: 'scheduledFrom',
      render: (scheduledFrom: string) => {
        return scheduledFrom ? extractTime(scheduledFrom) : 'No definido';
      },
    },
    {
      label: 'Hasta',
      field: 'scheduledTo',
      render: (scheduledTo: string) => {
        return scheduledTo ? extractTime(scheduledTo) : 'No definido';
      },
    },
    {
      label: 'Días de Repetición',
      field: 'days',
      render: (_: any, rowData: Record<string, any>) => {
        if (!rowData) return 'No definido';

        const days = [];
        if (rowData.monday === true || rowData.monday === 'true')
          days.push('Lunes');
        if (rowData.tuesday === true || rowData.tuesday === 'true')
          days.push('Martes');
        if (rowData.wednesday === true || rowData.wednesday === 'true')
          days.push('Miércoles');
        if (rowData.thursday === true || rowData.thursday === 'true')
          days.push('Jueves');
        if (rowData.friday === true || rowData.friday === 'true')
          days.push('Viernes');
        if (rowData.saturday === true || rowData.saturday === 'true')
          days.push('Sábado');
        if (rowData.sunday === true || rowData.sunday === 'true')
          days.push('Domingo');

        return days.length > 0 ? days.join(', ') : 'No definido';
      },
    },
    {
      label: 'Repetir hasta',
      field: 'repeat_until',
      render: (repeat_until: string) => {
        return repeat_until
          ? new Date(repeat_until).toLocaleDateString()
          : 'Fecha no válida';
      },
    },
  ];

  const renderActions = (rowData: Record<string, any>) => (
    <>
      {/*  <IconButton onClick={() => navigate(`${rowData.id}/edit`)}>
        <EditIcon />
      </IconButton> */}
      <IconButton onClick={() => onDeleteAssignment(rowData.id)}>
        <DeleteIcon />
      </IconButton>
    </>
  );

  return (
    
    <section className={styles.container}>
      <NavigateLinks
        actualPage='Asignaciones Recurrentes'
        breadcrumbs={breadcrumbs}
      />

      <div className={styles.actions}>
        <BackToModuleButton breadcrumbs={breadcrumbs} />
      </div>

      <SearchBar
        filters={filter}
        changeFilters={setFilter}
        hideDriverSearch={true}
        hideStatusFilter={true}
      />

      <div className={styles.content}>
        {isLoading || assignments.length > 0 ? (
          <Table
            isLoading={isLoading}
            columns={columns}
            data={assignments}
            renderActions={renderActions}
            pagination={{
              totalItems,
              totalPages,
              currentPage,
              rowsPerPage: pageSize,
              onPageChange: handlePageChange,
              onRowsPerPageChange: handlePageSizeChange,
            }}
          />
        ) : (
          <p>No existen asignaciones recurrentes registradas.</p>
        )}
      </div>
    </section>
  );
};

export default CalendarAssignmentsPage;
